
import React, { useState, useEffect } from 'react';
import { useAppSelector } from '@/hooks/useAppSelector';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send } from "lucide-react";
import { invokeEdgeFunction } from "@/utils/edgeFunctionUtils";
import { toast } from 'sonner';
import { formatVersionDate } from '@/utils/dateUtils';
import { versionToQuarter } from '@/utils/quarterlyEventsHelper';
import { useVersion } from '@/contexts/VersionContext';

interface PageAskAIProps {
  slug: string;
  subslug?: string | null;
}

interface VersionInfo {
  version: string;
  quarter: string;
  date: string;
}

interface GeminiResponse {
  answer: boolean;
  additional_info: {
    summary: string;
    key_points: string[];
    caveats?: string;
  };
}

const PageAskAI = ({ slug, subslug }: PageAskAIProps) => {
  const { selectedVersion } = useVersion();
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<GeminiResponse | null>(null);

  // Log the subslug we're filtering by
  console.log('🔍 [PageAskAI] Filtering events for slug:', slug, 'and subslug:', subslug);
  
  // Get events from both regular events and quarterly events, filtered by subslug if provided
  const events = useAppSelector(state => state.events.events.filter(event => {
    const slugMatch = event.slug === slug;
    let subslugMatch = true;
    
    if (subslug) {
      // If subslug is provided, only show events with matching page_subslug
      subslugMatch = event.page_subslug === subslug;
    } else {
      // If no subslug provided, only show events without a specific page_subslug
      subslugMatch = !event.page_subslug || event.page_subslug === '';
    }
    
    return slugMatch && subslugMatch;
  }));
  
  const quarterlyEvents = useAppSelector(state => state.quarterlyEvents.events.filter(event => {
    const slugMatch = event.slug === slug;
    let subslugMatch = true;
    
    if (subslug) {
      // If subslug is provided, only show events with matching page_subslug
      subslugMatch = event.page_subslug === subslug;
    } else {
      // If no subslug provided, only show events without a specific page_subslug
      subslugMatch = !event.page_subslug || event.page_subslug === '';
    }
    
    return slugMatch && subslugMatch;
  }));
  
  // Get the company from Redux store
  const companies = useAppSelector(state => state.supaCompanies.companies);
  const company = companies.find(c => c.slug === slug);
  
  // Get the specific page based on slug and subslug
  const companyPages = company?.pages || [];
  const currentPage = subslug 
    ? companyPages.find(p => p.subslug === subslug)
    : companyPages.find(p => !p.subslug) || (companyPages.length > 0 ? companyPages[0] : null);
  
  // For backward compatibility
  const pages = useAppSelector(state => state.supaPages.pages);
  const page = pages.find(p => p.slug === slug);

  // Create unique version info array
  const versionInfoMap = new Map<string, VersionInfo>();

  // Add page screenshot if it exists
  if (currentPage?.screenshotUrl && currentPage?.screenshotUrlVersion) {
    const quarter = versionToQuarter(currentPage.screenshotUrlVersion);
    versionInfoMap.set(currentPage.screenshotUrlVersion, {
      version: currentPage.screenshotUrlVersion,
      quarter,
      date: formatVersionDate(currentPage.screenshotUrlVersion) || '',
    });
  } else if (page?.screenshotUrl && page?.screenshotUrlVersion) {
    // Fallback to old page structure
    const quarter = versionToQuarter(page.screenshotUrlVersion);
    versionInfoMap.set(page.screenshotUrlVersion, {
      version: page.screenshotUrlVersion,
      quarter,
      date: formatVersionDate(page.screenshotUrlVersion) || '',
    });
  }

  // Add events data
  events.forEach(event => {
    // Add v1 version if not exists
    if (!versionInfoMap.has(event.v1)) {
      const quarter = versionToQuarter(event.v1);
      versionInfoMap.set(event.v1, {
        version: event.v1,
        quarter,
        date: formatVersionDate(event.v1) || '',
      });
    }

    // Add v2 version if not exists
    if (!versionInfoMap.has(event.v2)) {
      const quarter = versionToQuarter(event.v2);
      versionInfoMap.set(event.v2, {
        version: event.v2,
        quarter,
        date: formatVersionDate(event.v2) || '',
      });
    }
  });

  // Convert map to array and sort by version (descending to show newest first)
  const versions = Array.from(versionInfoMap.values())
    .sort((a, b) => b.version.localeCompare(a.version));

  const handleAskAI = async () => {
    if (!selectedVersion) {
      toast.error('Please select a version using the version selector above');
      return;
    }
    
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      const { data, error } = await invokeEdgeFunction('ask-gemini', {
        slug,
        version: selectedVersion,
        quarter: versionToQuarter(selectedVersion),
        question: prompt.trim()
      });

      if (error) {
        throw error;
      }

      // Parse the response according to the new Gemini function's format
      const response = data as GeminiResponse;
      if (response && response.additional_info) {
        setResult(response);
      } else {
        console.error('Unexpected response format:', data);
        throw new Error('Unexpected response format');
      }
    } catch (error) {
      console.error('Error calling AI:', error);
      toast.error('Failed to get AI response');
    } finally {
      setIsLoading(false);
    }
  };

  if (versions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4 bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200/50 p-6">
      <h3 className="text-lg font-semibold">Ask AI about this page</h3>
      
      <div className="space-y-4">
        {selectedVersion ? (
          <div className="text-sm text-gray-600">
            Analyzing version: {formatVersionDate(selectedVersion)} {versionToQuarter(selectedVersion)}
          </div>
        ) : (
          <div className="text-sm text-amber-600">
            Please select a version using the version selector above
          </div>
        )}

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Your Question</label>
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Ask anything about this pricing page version..."
            className="min-h-[100px]"
          />
        </div>

        <Button 
          onClick={handleAskAI} 
          disabled={isLoading || !selectedVersion || !prompt.trim()}
          className="w-full"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Ask AI
            </>
          )}
        </Button>

        {result && (
          <div className="mt-4 space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">Summary</h4>
              <p className="text-sm text-gray-700">{result.additional_info.summary}</p>
            </div>

            {result.additional_info.key_points.length > 0 && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-2">Key Points</h4>
                <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                  {result.additional_info.key_points.map((point, index) => (
                    <li key={index}>{point}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.additional_info.caveats && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-2">Additional Context</h4>
                <p className="text-sm text-gray-700">{result.additional_info.caveats}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageAskAI;
